import { Badge } from '@liveauctioneers/hammer-ui-core/badge';
import { cardFactory } from '@liveauctioneers/hammer-ui-core/card/factory';
import { CompleteAccountModalContext, ModalContext } from '@/redux/modules/modal';
import { createFavoriteButton } from '@liveauctioneers/hammer-ui-core/card/components/createFavoriteButton';
import { createImage } from '@liveauctioneers/hammer-ui-core/card/components/createImage';
import { createInfo } from '@liveauctioneers/hammer-ui-core/card/components/createInfo';
import { EventContext } from '@/redux/modules/analytics';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getItemUrl } from '@/utils/urls';
import { isItemSaved } from '@/redux/modules/item/saved/savedItems.selectors';
import { ItemSummaryWithRecommendation } from '@/types/item/ItemSummary';
import { LotRecommendationTrigger } from '@/utils/analytics/item/trackLotRecommendationClicked.analytics';
import { SubtleMonochromeLink, SubtleWeakLink } from '@liveauctioneers/hammer-ui-core/link';
import { toggleItemSave } from '@/redux/modules/item/saved/savedItems.actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecommendationTracking } from '@/hooks/useRecommendationTracking';
import { useRequireLogin } from '@/components/ShouldLogIn/hooks/useRequireLogin';
import {
    useTrackItemCardClickedPriceResults,
    useTrackSavedPriceResultsItem,
} from '@/hooks/kameleoon/tracking/useTrackPriceResultsPage';
import { useTrackItemSaved } from '@/hooks/kameleoon/tracking/useTrackItemSaved';
import ItemCardImage from '@/components/GridItemCard/Card/components/ItemCardImage/ItemCardImage';
import ItemCardPriceRow from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRow';

export const SimilarSoldItemCard = cardFactory<ItemSummaryWithRecommendation>({
    components: [
        createFavoriteButton({
            useIsFavorited: ({ itemId }) => {
                const isFavorite = useAppSelector((state) => isItemSaved(state, itemId));
                return isFavorite;
            },
            useSetFavorited: ({ itemId }) => {
                const trackItemSaved = useTrackItemSaved();
                const trackSavedPriceResultsItem = useTrackSavedPriceResultsItem();
                const dispatch = useAppDispatch();
                const isFavorite = useAppSelector((state) => isItemSaved(state, itemId));
                const verifyLogin = useRequireLogin();
                const location = useLocation();

                const modalContext = useMemo<ModalContext>(
                    () => ({
                        eventContext: EventContext.saveItem,
                        id: itemId,
                        itemId,
                        type: CompleteAccountModalContext.SaveItem,
                    }),
                    [itemId]
                );

                const onToggleSave = useCallback(() => {
                    if (!isFavorite) {
                        trackItemSaved();
                        trackSavedPriceResultsItem();
                    }

                    dispatch(toggleItemSave({ canHaveEasyShippingTag: false, itemId, location }));
                }, [dispatch, isFavorite, itemId, location, trackItemSaved, trackSavedPriceResultsItem]);

                return () => {
                    verifyLogin({
                        modalContext,
                        requireLogin: true,
                        runFunction: () => onToggleSave(),
                    });
                };
            },
        }),
        createInfo({
            className: 'line-clamp-0',
            useText: ({ isSold }) => {
                if (!isSold) {
                    return null;
                }

                return (
                    <div className="absolute left-xs top-xs z-50">
                        <Badge
                            emphasis="bold"
                            round
                            size="md"
                        >
                            <FormattedMessage id="similarSoldItemCarousel.badge" />
                        </Badge>
                    </div>
                );
            },
        }),
        createImage<ItemSummaryWithRecommendation>({
            ImageComponent: ({ itemId, recommendationPosition, recommendationType }) => {
                const trackItemCardClicked = useTrackItemCardClickedPriceResults();
                const { onRecommendationClick } = useRecommendationTracking({
                    itemId,
                    recommendationPosition,
                    recommendationType,
                });

                return (
                    <ItemCardImage
                        className="h-[172px] w-[172px]"
                        itemId={itemId}
                        onNavigate={() => {
                            trackItemCardClicked();
                            onRecommendationClick(LotRecommendationTrigger.ImageClick);
                        }}
                    />
                );
            },
        }),
        { spacerPx: 8 },
        createInfo({
            className: 'line-clamp-0',
            useText: ({ itemId, recommendationPosition, recommendationType, shortDescription, title }) => {
                const trackItemCardClicked = useTrackItemCardClickedPriceResults();
                const { onRecommendationClick } = useRecommendationTracking({
                    itemId,
                    recommendationPosition,
                    recommendationType,
                });

                return (
                    <div className="flex flex-col gap-[2px]">
                        <SubtleMonochromeLink
                            className="line-clamp-2"
                            href={getItemUrl(itemId, title)}
                            onClick={() => {
                                trackItemCardClicked();
                                onRecommendationClick(LotRecommendationTrigger.TitleClick);
                            }}
                        >
                            {title}
                        </SubtleMonochromeLink>
                        <SubtleWeakLink
                            className="line-clamp-2 text-text-secondary"
                            href={getItemUrl(itemId, title)}
                            onClick={() => {
                                trackItemCardClicked();
                                onRecommendationClick(LotRecommendationTrigger.TitleClick);
                            }}
                        >
                            {shortDescription}
                        </SubtleWeakLink>
                    </div>
                );
            },
        }),
        { spacerPx: 2 },
        createInfo({
            className: 'line-clamp-0',
            useText: ({ bidCount, itemId }) => {
                const trackItemCardClicked = useTrackItemCardClickedPriceResults();
                const [linkHover, setLinkHover] = useState(false);

                const enterLinkHoverState = useCallback(() => {
                    setLinkHover(true);
                }, []);

                const leaveLinkHoverState = useCallback(() => {
                    setLinkHover(false);
                }, []);

                return (
                    <ItemCardPriceRow
                        isHovered={linkHover}
                        itemId={itemId}
                        onMouseEnter={enterLinkHoverState}
                        onMouseLeave={leaveLinkHoverState}
                        onNavigate={() => trackItemCardClicked()}
                        showBidCount={Boolean(bidCount)}
                    />
                );
            },
        }),
    ],
    name: 'SimilarSoldItemCard',
});
